/**
 * Telegram-based admin authentication
 * With session caching for fast reopens
 */

const API_URL = process.env.REACT_APP_API_URL || 'https://api.kit8exchange.com';
const SESSION_KEY = 'admin_session';
const SESSION_TTL = 30 * 60 * 1000; // 30 минут

function getCachedSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (Date.now() > cached.expiry) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    return cached;
  } catch {
    return null;
  }
}

function cacheSession(token, userId, user) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({
      token,
      userId,
      user,
      expiry: Date.now() + SESSION_TTL,
    }));
  } catch { /* ignore */ }
}

export async function authenticateAdmin() {
  const tg = window.Telegram?.WebApp;

  if (!tg?.initData) {
    throw new Error('NO_TELEGRAM');
  }

  tg.ready();
  tg.expand();
  // Disable swipe-to-close so scrolling works properly
  try { tg.disableVerticalSwipes(); } catch {}
  try { tg.enableClosingConfirmation(); } catch {}
  try { tg.setHeaderColor('#0f172a'); } catch {}
  try { tg.setBackgroundColor('#0f172a'); } catch {};

  const user = tg.initDataUnsafe?.user;
  if (!user?.id) {
    throw new Error('NO_USER_DATA');
  }

  // Try cached session first
  const cached = getCachedSession();
  if (cached && cached.userId === String(user.id)) {
    return cached;
  }

  // Fresh auth: session + admin check in parallel where possible
  const sessionResponse = await fetch(`${API_URL}/api/auth/session`, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      initData: tg.initData,
      userId: user.id,
      username: user.username || '',
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      photoUrl: user.photo_url || '',
    }),
  });

  if (!sessionResponse.ok) {
    throw new Error('AUTH_FAILED');
  }

  const sessionData = await sessionResponse.json();
  if (!sessionData.success || !sessionData.sessionToken) {
    throw new Error('AUTH_FAILED');
  }

  const token = sessionData.sessionToken;
  const userId = String(user.id);

  // Verify admin status
  const adminResponse = await fetch(`${API_URL}/api/user/is-admin`, {
    method: 'GET',
    credentials: 'omit',
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-User-ID': userId,
      'Content-Type': 'application/json',
    },
  });

  if (!adminResponse.ok) {
    throw new Error('NOT_ADMIN');
  }

  const adminData = await adminResponse.json();
  if (!adminData.success || !adminData.isAdmin) {
    throw new Error('NOT_ADMIN');
  }

  const result = {
    token,
    userId,
    user: {
      id: user.id,
      username: user.username,
      firstName: user.first_name,
      lastName: user.last_name,
      photoUrl: user.photo_url,
    },
  };

  cacheSession(token, userId, result.user);

  return result;
}
